const STYLE_ID = 'blufin-suspend-transitions';
const STYLE_RULES = `*,*::before,*::after{transition:none!important;animation-duration:0s!important;animation-delay:0s!important;}`;
/**
 * How many suppression windows are still open, carried ON the tag rather than in a module variable.
 * `ThemeProvider` syncs theme, mode, density, font and reduce-motion in five separate layout effects that all
 * fire in the same commit on mount, so `run` is called five times before the first `requestAnimationFrame`
 * callback executes — five `<style>` elements sharing one non-unique `id` if each call appends its own.
 * The count lives on the node so its lifetime is exactly the node's: whatever removes the tag (a test
 * resetting the DOM, a document replacement) resets the count with it, which a module-scope counter would
 * survive and then strand the next window open forever.
 */
const OPEN_ATTRIBUTE = 'data-open';
/**
 * Backstop for the frame-based release. Chromium pauses `requestAnimationFrame` while a window is hidden or
 * occluded (an Electron window behind another app, an OS light/dark switch landing while backgrounded), and a
 * window left open there disables every transition in the document. Timers still fire (throttled to ~1s), so
 * whichever of the two runs first closes the window.
 */
const RELEASE_TIMEOUT_MS = 100;

export interface ISuspendTransitions {
  run(mutate: () => void): void;
}

/**
 * Suspends CSS transitions and animations document-wide while a mutating
 * callback runs, so the resulting style change paints instantly. Implementation
 * note: injects a <style> tag in <head> rather than toggling a class on <html>
 * so it never feeds back into any MutationObserver watching the documentElement.
 * Overlapping calls share that one tag and reference-count it.
 */
export const SuspendTransitions: ISuspendTransitions = {
  /**
   * Runs `mutate` with transitions suspended. Forces a layout flush after the
   * mutation so the new styles apply with transitions still off, then removes
   * the suppression style on the next animation frame (or after `RELEASE_TIMEOUT_MS`
   * when frames are paused) — or, when other calls are still inside their own
   * window, leaves it for the last of them to remove.
   * @param mutate Callback that performs the style/class mutation.
   */
  run(mutate: () => void): void {
    if (typeof document === 'undefined') {
      mutate();
      return;
    }
    _openWindow();
    let released = false;
    const release = (): void => {
      if (released) return;
      released = true;
      _closeWindow();
    };
    let cleanupScheduled = false;
    try {
      mutate();
      /** Force layout flush so the new styles apply with transitions still suspended. */
      void document.documentElement.offsetHeight;
      requestAnimationFrame(release);
      setTimeout(release, RELEASE_TIMEOUT_MS);
      cleanupScheduled = true;
    } finally {
      /** A throwing `mutate` never reaches the frame callback, so its window has to close here instead. */
      if (!cleanupScheduled) release();
    }
  }
};

/** Closes one window on the LIVE tag, removing it once none are left. A tag already gone is nothing to do. */
const _closeWindow = (): void => {
  const style = document.getElementById(STYLE_ID);
  if (style === null) return;
  const open = Number(style.getAttribute(OPEN_ATTRIBUTE) ?? '1') - 1;
  if (open > 0) style.setAttribute(OPEN_ATTRIBUTE, String(open));
  else style.remove();
};

/** Opens one window, creating the shared tag the first time and otherwise just incrementing its count. */
const _openWindow = (): void => {
  const existing = document.getElementById(STYLE_ID);
  if (existing !== null) {
    existing.setAttribute(OPEN_ATTRIBUTE, String(Number(existing.getAttribute(OPEN_ATTRIBUTE) ?? '0') + 1));
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.setAttribute(OPEN_ATTRIBUTE, '1');
  style.appendChild(document.createTextNode(STYLE_RULES));
  document.head.appendChild(style);
};
